import React, { useState } from 'react'
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  Link,
  CircularProgress,
  makeStyles
} from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import axios from 'axios'

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  logo: {
    maxHeight: 80,
    marginBottom: theme.spacing(2),
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  nameRow: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  consentBox: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2.5),
    marginTop: theme.spacing(2),
  },
  checkboxLabel: {
    alignItems: 'flex-start',
    marginLeft: 0,
  },
  checkbox: {
    paddingTop: 2,
  },
  disclosureList: {
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
    marginBottom: 0,
    '& li': {
      marginBottom: theme.spacing(0.5),
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
    },
  },
  submitBtn: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5),
  },
  footer: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  successContainer: {
    textAlign: 'center',
    padding: theme.spacing(6, 2),
  },
  successIcon: {
    fontSize: 64,
    color: theme.palette.success.main || '#4caf50',
    marginBottom: theme.spacing(2),
  },
}))

const PRIVACY_POLICY_URL = '/online/app-privacy-policy'
const COMPANY_NAME = 'Mariani Premier Group'
const COMPANY_WEBSITE = 'www.hmiofaz.com'

const SMSConsentPage = () => {
  const classes = useStyles()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [consentChecked, setConsentChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '')
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
  }

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value)
    setPhone(formatted)
  }

  const isValid = firstName.trim() && lastName.trim() && phone.replace(/\D/g, '').length === 10 && consentChecked

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isValid) return

    setSubmitting(true)
    setError('')

    try {
      await axios.post('/api/sms-consent', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.replace(/\D/g, ''),
        consentedAt: new Date().toISOString(),
      })
      setSubmitted(true)
    } catch (err) {
      setError('Something went wrong. Please try again or contact us directly.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <Container className={classes.root} maxWidth="sm" component={Paper} elevation={2}>
        <Box p={4} className={classes.successContainer}>
          <CheckCircleOutlineIcon className={classes.successIcon} />
          <Typography variant="h5" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1" color="textSecondary">
            You have successfully opted in to receive SMS messages from {COMPANY_NAME}.
            You can opt out at any time by texting STOP.
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container className={classes.root} maxWidth="sm" component={Paper} elevation={2}>
      <Box p={4}>
        {/* Header */}
        <Box className={classes.header}>
          <Typography variant="h4" component="h1" gutterBottom>
            SMS Notifications Sign-Up
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Opt in to receive SMS notifications from {COMPANY_NAME} (dba HMI of AZ).
          </Typography>
        </Box>

        <Divider />

        {/* Call-to-Action disclosure — shown BEFORE phone input */}
        <Box mt={3} mb={2} p={2} style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4 }}>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>
            What you&apos;re signing up for
          </Typography>
          <Typography variant="body2" color="textSecondary">
            By submitting this form and checking the consent box below, you agree to receive SMS text messages from{' '}
            {COMPANY_NAME} for account notifications, two-factor authentication (2FA) codes, shift updates, and
            operational alerts. Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong>{' '}
            to cancel or <strong>HELP</strong> for help. SMS consent is <strong>not a condition of purchase or
            employment</strong>, and we do not share your phone number or consent with third parties or affiliates
            for marketing purposes.
          </Typography>
        </Box>

        {/* Form */}
        <form onSubmit={handleSubmit} className={classes.section}>
          <Box className={classes.form}>
            <Box className={classes.nameRow}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Box>

            <TextField
              label="Mobile Phone Number"
              variant="outlined"
              fullWidth
              required
              value={phone}
              onChange={handlePhoneChange}
              placeholder="(555) 555-5555"
              inputProps={{ maxLength: 14 }}
              helperText="US mobile number only. Standard message and data rates may apply."
            />

            {/* SMS Consent Checkbox — unchecked by default, voluntary */}
            <Box className={classes.consentBox}>
              <FormControlLabel
                className={classes.checkboxLabel}
                control={
                  <Checkbox
                    className={classes.checkbox}
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    color="primary"
                    inputProps={{ 'aria-label': 'SMS consent checkbox — voluntary opt-in' }}
                  />
                }
                label={
                  <Typography variant="body2">
                    <strong>I agree to receive SMS text messages from {COMPANY_NAME}</strong> at the phone number
                    provided above. Message types include account notifications, two-factor authentication codes,
                    shift updates, and operational alerts. Message frequency varies. Message and data rates may
                    apply. Reply STOP to opt out at any time; reply HELP for help. Consent is not a condition of
                    purchase or employment.
                  </Typography>
                }
              />
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 12, paddingLeft: 32 }}>
                We do not sell, rent, or share your SMS opt-in data, phone number, or consent with third parties or
                affiliates for marketing or promotional purposes. For full details, see our{' '}
                <Link href={PRIVACY_POLICY_URL} className={classes.link} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link href={`${PRIVACY_POLICY_URL}#request`} className={classes.link} target="_blank" rel="noopener noreferrer">
                  SMS Terms of Service
                </Link>.
              </Typography>
            </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              className={classes.submitBtn}
              disabled={!isValid || submitting}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'I Agree & Sign Up'}
            </Button>
          </Box>
        </form>

        {/* Footer */}
        <Box className={classes.footer}>
          <Divider />
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              &copy; {new Date().getFullYear()} Mariani Enterprises LLC (dba HMI of AZ). All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  )
}

export default SMSConsentPage
